<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { userModule } from '@/store/user/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { studentModule } from '@/store/student/module'
import AssignmentCard from '@/components/Assignments/AssignmentCard.vue'
import UIKit from '@pocketprep/ui-kit'
import type { Study } from '@pocketprep/types'
import { useRouter } from 'vue-router'

const router = useRouter()

const {
    SidePanel: SidePanel,
    Button: PocketButton,
    EmptyState,
} = UIKit

const { assignments } = defineProps<{
    assignments: Study.Class.PrebuiltQuizJSON[]
}>()

const emit = defineEmits<{
    close: []
}>()

const isLoading = ref(false)
const selectedAssignmentId = ref<string | null>(null)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const currentUser = computed(() => {
    return userModule.state.user
})

const studentOrgNamesArray = computed(() => {
    return studentModule.getters.getStudentOrgNames()
})

const studentOrgNames = computed(() => {
    if (studentOrgNamesArray.value.length === 1) {
        return studentOrgNamesArray.value.join('')
    } else if (studentOrgNamesArray.value.length === 2) {
        return studentOrgNamesArray.value.join(', and ')
    } else {
        const allButLastOrg = studentOrgNamesArray.value.slice(0, -1).join(', ')
        const lastOrg = studentOrgNamesArray.value.slice(-1)
        return `${allButLastOrg}, and ${lastOrg}`
    }
})

const emptyStateAssignmentMessage = computed(() => {
    return `Assignments are quizzes assigned to you by your 
        organization${studentOrgNamesArray.value.length > 1 ? 's,' : ','} ${studentOrgNames.value}.`
})

onMounted(async () => {
    isLoading.value = true
    if (currentUser.value) {
        await Promise.all([
            bundleModule.actions.fetchBundles(),
            examMetadataModule.actions.fetchExamMetadata(),
            userModule.actions.fetchUserData(),
            userExamMetadataModule.actions.fetchUserExamMetadata(),
        ])
    }

    // Preselect an assignment if there is only one assignment available
    if (assignments.length === 1) {
        selectedAssignmentId.value = assignments[0].objectId
    }

    isLoading.value = false
})


const selectAssignment = (id: string) => {
    selectedAssignmentId.value = id
}

const openAssignment = async () => {
    await router.push({
        name: 'assignment-intro',
        query: { assignment: selectedAssignmentId.value },
    })
}

</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            :key="`assignmentsSidePanel-${String($.vnode.key)}`"
            name="Assignments"
            class="assignments-side-panel"
            width="medium"
            :is-dark-mode="isDarkMode"
            :show-cancel-button="assignments.length"
            @close="emit('close')"
        >
            <div
                v-if="assignments && assignments.length"
                class="assignments-side-panel__info"
            >
                <div
                    v-dark
                    class="assignments-side-panel__title"
                >
                    {{ assignments.length }} Available Assignment{{ assignments.length > 1 ? 's' : '' }}
                </div>
                <div 
                    v-dark 
                    class="assignments-side-panel__assignment-org-label"
                >
                    Assignments are quizzes assigned to you by your 
                    organization{{ studentOrgNamesArray.length > 1 ? 's,': ',' }}
                    {{ studentOrgNames ? `${studentOrgNames}` : '' }}.
                </div>
                <div
                    class="assignments-side-panel__assignment-cards"
                >
                    <AssignmentCard
                        v-for="(assignment) in assignments"
                        :key="assignment.objectId"
                        :assignment="assignment"
                        :is-selected="selectedAssignmentId === assignment.objectId"
                        @keydown.enter="selectAssignment(assignment.objectId)"
                        @click="selectAssignment(assignment.objectId)"
                    />
                </div>
                <div class="assignments-side-panel__assignment-reminders">
                    <div v-dark class="assignments-side-panel__assignment-reminders-label">
                        Reminders
                    </div>
                    <ul class="assignments-side-panel__assignment-reminders-list">
                        <li
                            v-dark 
                            class="assignments-side-panel__assignment-reminders-list-item"
                        >
                            After the due date, the assignment will no longer be available.
                        </li>
                        <li v-dark class="assignments-side-panel__assignment-reminders-list-item">
                            You can submit each assignment only once.
                        </li>
                        <li v-dark class="assignments-side-panel__assignment-reminders-list-item">
                            Completed assignments can be found in the Study Activity
                            section in the Stats tab.
                        </li>
                    </ul>
                </div>
            </div>
            <div
                v-else
                class="assignments-side-panel__empty-state" 
            >
                <EmptyState
                    class="assignments-side-panel__empty-state-message"
                    state="assignment"
                    :title="'0 Available Assignments'"
                    :message="emptyStateAssignmentMessage"
                    backgroundColor="white"
                    :is-dark-mode="isDarkMode"
                />
            </div>
            <template #action>
                <PocketButton
                    v-if="assignments && assignments.length"
                    :is-dark-mode="isDarkMode"
                    :is-loading="isLoading"
                    :disabled="!selectedAssignmentId"
                    @click="openAssignment"
                >
                    Open Assignment
                </PocketButton>
                <PocketButton
                    v-else
                    :is-dark-mode="isDarkMode"
                    :is-loading="isLoading"
                    @click="emit('close')"
                >
                    Close
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.assignments-side-panel {
    &__title {
        color: $brand-black;
        font-size: 18px;
        font-weight: 600;
        line-height: 25px;
        padding-left: 16px;
        margin-bottom: 6px;

        &--dark {
            color: $barely-background;
        }
    }

    &__assignment-org-label {
        color: $brand-black;
        font-size: 15px;
        font-weight: 500;
        line-height: 22px;
        margin-bottom: 24px;
        padding-left: 16px;

        &--dark {
            color: $fog;
        }
    }

    &__assignment-reminders {
        margin-top: 24px;
        padding-left: 16px;
        padding-right: 16px;
    }

    &__assignment-reminders-label {
        color: $brand-black;
        font-size: 16px;
        font-weight: 600;
        line-height: 22px;
        margin-bottom: 10px;

        &--dark {
            color: $barely-background;
        }
    }

    &__assignment-reminders-list {
        list-style-type: disc;
        margin-left: -12px;
        margin-top: 0;
    }

    &__assignment-reminders-list-item {
        color: $brand-black;
        font-size: 15px;
        font-weight: 500;
        line-height: 22px;
        margin-bottom: 4px;

        &--dark {
            color: $fog;
        }
    }

    &__empty-state {
        display: flex;
        justify-content: center;
    }

    :deep(.uikit-side-panel__content) {
        padding: 24px;
    }

    :deep(.uikit-side-panel__head) {
        padding-left: 38px;
    }

    :deep(.uikit-empty-state) {
        @include breakpoint(black-bear) {
            padding: 0;
        }
    }
    :deep(.uikit-empty-state__message) {
        @include breakpoint(black-bear) {
            padding: 0;
        }
    }
}
</style>
